import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import useApi from "modules/account"
import useDictionaryApi from "modules/dictionary"
import secureLocalStorage from "react-secure-storage"
import { ENVIORNMENTS, SMASH_SESSION, USER_TYPES } from "common/constants"
import { TEACHER_VIEWS } from "pages/student/activity/common/constants"
import i18n from "i18n"
import moment from "moment"
import * as Sentry from "@sentry/react"
import { LOCAL_STORAGE_PREFIX } from "../common/constants"
import { jwtDecode } from "jwt-decode"
import { useThemeContext } from "./ThemeContext"
import { isRunningFromIframe, isSharedRoutePath } from "common/functions"
import useSound from "use-sound"
import { logout as logoutSound } from "utils/sounds"
import useUIApi from "modules/uiSettings"
import useSystemDarkMode from "hooks/useSystemDarkMode"
import storage from "utils/persistentStorage"

import { StudentAuthContext } from "./StudentAuthContext"
import { TeacherAuthContext } from "./TeacherAuthContext"
import { routes } from "common/navigationRoutes"
import { logger } from "utils/logger"

export const AuthContext = React.createContext()

export const useAuthContext = () => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider")
  }
  const studentContext = React.useContext(StudentAuthContext) || {}
  const teacherContext = React.useContext(TeacherAuthContext) || {}

  const roleContexts = [studentContext, teacherContext]
  const activeRoleContext = roleContexts.find(
    (ctx) => ctx.isRestoreCompleted !== undefined
  )
  const isRestoring = activeRoleContext
    ? !activeRoleContext.isRestoreCompleted
    : false
  const isLoading = context.isLoading || isRestoring

  return useMemo(() => {
    return {
      ...context,
      ...studentContext,
      ...teacherContext,
      isLoading,
    }
  }, [context, studentContext, teacherContext, isLoading])
}

const getTokenFromParent = (timeoutMs = 5000) => {
  // Try direct parent access first (same-origin), fall back to postMessage (cross-origin)
  try {
    const directToken = window?.parent?.getAuthToken()
    if (directToken && directToken !== "") {
      return Promise.resolve(directToken)
    }
  } catch (error) {
    // Cross-origin or function not available, fall back to postMessage
  }

  return new Promise((resolve) => {
    const handler = (event) => {
      if (event.data?.type === "AUTH_TOKEN_RESPONSE") {
        window.removeEventListener("message", handler)
        resolve(event.data?.token || "")
      }
    }
    window.addEventListener("message", handler)
    window.parent.postMessage({ type: "AUTH_TOKEN_REQUEST" }, "*")

    setTimeout(() => {
      window.removeEventListener("message", handler)
      resolve("")
    }, timeoutMs)
  })
}

const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [queryParams] = useSearchParams()
  const navigate = useNavigate()
  const { getUserAllInformation, setAllowTutorials, exchangeToken } = useApi()
  const [userData, setUserData] = useState({
    userId: -1,
    username: "",
    lastName: "",
    name: "",
    parentCode: "",
    language: 2,
    avatar: "https://login.smasheducation.com/avatars//default.png",
    allowTutorials: true,
  })
  const { getTranslation } = useDictionaryApi()
  const { setFontFamily, setFontSize, setBgColor, setMode } = useThemeContext()
  const [playLogout] = useSound(logoutSound, { volume: 0.5, html5: false })
  const { setUiSettings } = useUIApi()
  const [cacheRewardsNotifications, setCacheRewardsNotifications] =
    useState(false)
  const isSystemDarkMode = useSystemDarkMode()

  const loadMomentLocale = async (langCode) => {
    if (!langCode) return
    const code = langCode.toLowerCase()
    try {
      // Standard moment locales
      await import(`moment/locale/${code}`)
      moment.locale(code)
    } catch (e) {
      // Try base language (e.g., 'es' if 'es-mx' fails)
      const baseCode = code.split("-")[0]
      if (baseCode !== code) {
        try {
          await import(`moment/locale/${baseCode}`)
          moment.locale(baseCode)
        } catch (e2) {
          // Check for custom locales
          try {
            let customLocale = null
            switch (code) {
              case "no":
                customLocale = await import("common/moment/locales/no")
                break
              case "so":
                customLocale = await import("common/moment/locales/so")
                break
              case "ps":
                customLocale = await import("common/moment/locales/ps")
                break
              case "pa":
                customLocale = await import("common/moment/locales/pa")
                break
              case "fil":
                customLocale = await import("common/moment/locales/fil")
                break
              case "zh-hans":
                customLocale = await import("common/moment/locales/zh-hans")
                break
              case "zh-hk":
                customLocale = await import("common/moment/locales/zh-hk")
                break
              default:
                break
            }
            if (customLocale) {
              moment.defineLocale(code, customLocale.default)
              moment.locale(code)
            } else {
              moment.locale("en") // Final fallback
            }
          } catch (e3) {
            console.warn(`Could not load moment locale for ${code}`)
          }
        }
      }
    }
  }

  const createUserSession = async () => {
    // await setCurrentScreen("activity")
  }

  const checkLanguage = async (profileLanguage) => {
    await storage.setItem("smashLanguageId", profileLanguage)
    let versionInfo = null
    try {
      const vResponse = await fetch(
        `/${process.env.REACT_APP_PUBLIC_URL}translations_version.json?v=${process.env.REACT_APP_VERSION}`
      )
      if (vResponse.ok) {
        const versions = await vResponse.json()
        versionInfo = versions[profileLanguage] || null
      }
    } catch (error) {
      console.warn("Could not fetch translations_version.json", error)
    }

    if (versionInfo) {
      const cacheKey = `${LOCAL_STORAGE_PREFIX}i18next_res_${versionInfo.code}-translations`
      const _cache = await storage.getItem(cacheKey)
      if (_cache) {
        try {
          const translations = _cache
          if (versionInfo.version === translations.i18nVersion) {
            console.log("load from cached version file")
            i18n.addResourceBundle(
              versionInfo.code,
              "translations",
              translations,
              true,
              true
            )
            i18n.changeLanguage(versionInfo.code)
            await loadMomentLocale(versionInfo.code)
            return
          }
        } catch (e) {
          console.warn("Error parsing translation cache", e)
        }
      }
    }

    const resources = await getTranslation({ id: profileLanguage })
    if (resources) {
      const _cache = await storage.getItem(
        `${LOCAL_STORAGE_PREFIX}i18next_res_${resources.code}-translations`
      )
      let hasResources = false
      if (_cache) {
        const translations = _cache
        const version = resources.fileVersion
        console.log(`version: ${version} - ${translations.i18nVersion}`)
        if (version === translations.i18nVersion) {
          console.log("load from cache")
          i18n.addResourceBundle(
            resources.code,
            "translations",
            translations,
            true,
            true
          )
          hasResources = true
        }
      }
      if (!hasResources || _cache === null || _cache === undefined) {
        const translations = resources.resources
        i18n.addResourceBundle(
          resources.code,
          "translations",
          translations,
          true,
          true
        )
        await i18n.loadLanguages(resources.code, (err) => {
          if (err) {
            console.log("error loading resources")
          } else {
            console.log("resources have been loaded")
          }
        })
        await i18n.reloadResources()
        translations.i18nVersion = resources.fileVersion
        translations.i18nStamp = Date.now()
        await storage.setItem(
          `${LOCAL_STORAGE_PREFIX}i18next_res_${resources.code}-translations`,
          translations
        )
      }
      i18n.changeLanguage(resources.code)
      console.log(i18n.getResource(resources.code, "translations", "Missions"))
      await loadMomentLocale(resources.code)
    } else {
      i18n.changeLanguage("en-US")
      await loadMomentLocale("en-US")
    }
  }

  const loadLocales = useCallback(() => {
    // Basic locales already included or custom ones handled by loadMomentLocale
    moment.defineLocale("en-us", { parentLocale: "en" })
    moment.defineLocale("fr-fr", { parentLocale: "fr" })
  }, [])

  const updateUser = useCallback(
    async (data) => {
      const { userId = -1, forceRefetch = false } = data
      let user = null
      if (userId > 0 && !forceRefetch) {
        user = await storage.getItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`)
      }
      if (!user || user === null) {
        user = await getUserAllInformation()
        if (!user) {
          secureLocalStorage.removeItem("smashAuthToken")
          secureLocalStorage.removeItem(SMASH_SESSION)
          window.location.href = "/login?session=invalid-token"
          return
        }
        if (user && userId > 0) {
          await storage.setItem(
            `${LOCAL_STORAGE_PREFIX}userAll_${userId}`,
            user
          )
        }
      }
      if (user) {
        const runningFromIframe = isRunningFromIframe()
        const isCreateAccountPage = window.location.pathname
          .toLowerCase()
          .includes(routes.CREATE_ACCOUNT)
        if (
          Number(process.env.REACT_APP_PROFESSOR_LOGIN_VERSION) === 1 &&
          user.userTypeId === USER_TYPES.TEACHER &&
          !runningFromIframe &&
          !isCreateAccountPage &&
          !isSharedRoutePath()
        ) {
          logger.log(
            "Teacher must run in iframe under Login Version 1. Rejecting user update."
          )
          logout()
          return
        }
        const userId = user?.userId || user?.id
        Sentry.setUser({ userId })
        setUserData(user)
        if (user?.uiSettings?.theme) {
          const currentTheme = user.uiSettings.theme?.toLowerCase()
          if (currentTheme !== "auto") {
            setMode(currentTheme)
          } else {
            setMode(isSystemDarkMode ? "dark" : "light")
          }
          const isDarkMode =
            currentTheme === "dark" ||
            (currentTheme === "auto" && isSystemDarkMode)
          localStorage.setItem("smash-prefers-dark-mode", isDarkMode.toString())
          if (isDarkMode) {
            // set in html class dark
            document.documentElement.classList.add("dark-mode")
          } else {
            // remove in html class dark
            document.documentElement.classList.remove("dark-mode")
          }
        }
        // change user language
        const viewMode = parseInt(queryParams.get("view")) || 0
        const isTeacherViewMode = TEACHER_VIEWS.includes(viewMode)
        const profileLanguage = isTeacherViewMode
          ? user?.secondaryLanguage
          : user?.language
        await checkLanguage(profileLanguage)
      } else {
        await checkLanguage(2)
        localStorage.setItem("smash-prefers-dark-mode", "false")
        document.documentElement.classList.remove("dark-mode")
        setMode("light")
        setBgColor("white")
        setFontFamily("Poppins Regular")
        setFontSize("18px")
        Sentry.setUser(null)
        secureLocalStorage.removeItem("smashAuthToken")
        secureLocalStorage.removeItem(SMASH_SESSION)
      }
    },
    [
      getUserAllInformation,
      setMode,
      isSystemDarkMode,
      queryParams,
      checkLanguage,
      setBgColor,
      setFontFamily,
      setFontSize,
    ]
  )

  const updateUserData = useCallback(
    async ({ token = "", newUserData = null }) => {
      if (ENVIORNMENTS.DEVELOPMENT === process.env.REACT_APP_ENVIORNMENT) {
        console.log("~ token:", token)
      }
      if (token && token !== "") {
        // update user token
        secureLocalStorage.setItem("smashAuthToken", token)
      }
      if (newUserData !== null) {
        const runningFromIframe = isRunningFromIframe()
        const isTeacher = newUserData?.userTypeId === USER_TYPES.TEACHER
        const isCreateAccountPage = window.location.pathname
          .toLowerCase()
          .includes(routes.CREATE_ACCOUNT)
        if (
          Number(process.env.REACT_APP_PROFESSOR_LOGIN_VERSION) === 1 &&
          isTeacher &&
          !runningFromIframe &&
          !isCreateAccountPage &&
          !isSharedRoutePath()
        ) {
          logger.log(
            "Teacher must run in iframe under Login Version 1. Rejecting updateUserData."
          )
          logout()
          return
        }
        const mergedUser = {
          ...userData,
          ...newUserData,
        }
        setUserData(mergedUser)
        if (
          newUserData.allowTutorials !== undefined &&
          newUserData.allowTutorials !== userData?.allowTutorials
        ) {
          setAllowTutorials({ allow: newUserData.allowTutorials })
        }
        if (
          newUserData?.uiSettings !== undefined &&
          newUserData?.uiSettings?.theme !== userData?.uiSettings?.theme
        ) {
          setUiSettings(newUserData?.uiSettings)
        }
        if (newUserData?.uiSettings !== undefined) {
          if (newUserData?.uiSettings?.theme?.toLowerCase() !== "auto") {
            setMode(newUserData?.uiSettings?.theme?.toLowerCase() || "light")
          } else {
            setMode(isSystemDarkMode ? "dark" : "light")
          }
        }
        // update user in local storage
        try {
          await storage.setItem(
            `${LOCAL_STORAGE_PREFIX}userAll_${mergedUser.userId}`,
            mergedUser
          )
        } catch (error) {
          console.log("Error updating user data in local storage", error)
        }

        // change user language
        const viewMode = parseInt(queryParams.get("view")) || 0
        const isTeacherViewMode = TEACHER_VIEWS.includes(viewMode)
        const profileLanguage = isTeacherViewMode
          ? mergedUser?.secondaryLanguage
          : mergedUser?.language
        const currentLanguage = isTeacherViewMode
          ? userData?.secondaryLanguage
          : userData?.language

        if (profileLanguage && profileLanguage !== currentLanguage) {
          await checkLanguage(profileLanguage)
        }
      }
    },
    [
      userData,
      setAllowTutorials,
      setUiSettings,
      setMode,
      isSystemDarkMode,
      queryParams,
      checkLanguage,
    ]
  )

  const validateEmbedModeMismatch = async (data = null) => {
    const iframeFlag = isRunningFromIframe()
    const isRealIframe = (() => {
      try {
        return window.self !== window.top
      } catch {
        return false
      }
    })()
    const isInIframe = iframeFlag || isRealIframe
    if (isSharedRoutePath()) {
      return false
    }
    const embedModeKey = data
      ? Object.keys(data).find((k) => k.startsWith("embed_mode"))
      : null
    const embedMode = (embedModeKey ? data[embedModeKey] : null) ?? "both"
    logger.log("embedMode:", embedMode)
    if (embedMode === "iframe_only" && !isInIframe) {
      const isOnLogin = window.location.pathname
        .toLowerCase()
        .includes(routes.LOGIN)
      logger.log("clearing out session - embed mode mismatch")
      await clearSession()
      window.location.href = `/login${
        isOnLogin ? "" : "?session=embed_mode_mismatch"
      }`
      return true
    }
    return false
  }

  useEffect(() => {
    const getUser = async () => {
      const authOrigin = queryParams.get("authOrigin")
      const embedToken = queryParams.get("token")
      const isEmbedPath = window.location.pathname.startsWith("/embed")
      const isInvoicePage = window.location.pathname
        .toLowerCase()
        .includes("payments")
      const iframeFlag = isRunningFromIframe()

      if (isEmbedPath && embedToken && embedToken !== "") {
        secureLocalStorage.setItem("smashAuthToken", embedToken)
        let userId = -1
        let decodedPayload = null
        try {
          decodedPayload = jwtDecode(embedToken)
          userId = decodedPayload?.id || -1
        } catch (error) {
          if (typeof window?.onAuthError === "function") {
            window.onAuthError("Invalid token")
          }
        }
        const result = await validateEmbedModeMismatch(decodedPayload)
        if (result) {
          return
        }
        if (userId > 0) {
          try {
            await updateUser({ userId })
            secureLocalStorage.removeItem(SMASH_SESSION)
            await createUserSession()

            if (typeof window?.onAuthSuccess === "function") {
              window.onAuthSuccess(userId)
            }
          } catch (error) {
            if (typeof window?.onAuthError === "function") {
              window.onAuthError(error.message)
            }
          }
        }
        // Remove token from URL to avoid keeping it in history
        const url = new URL(window.location.href)
        url.searchParams.delete("token")
        window.history.replaceState({}, document.title, url.toString())
      } else if (authOrigin === "parent" && iframeFlag) {
        const token = await getTokenFromParent()
        if (token && token !== "") {
          secureLocalStorage.setItem("smashAuthToken", token)
          let userId = -1
          let decodedPayload = null
          try {
            decodedPayload = jwtDecode(token)
            userId = decodedPayload?.id || -1
          } catch (error) {
            console.error("Token inválido", error)
          }
          const result = await validateEmbedModeMismatch(decodedPayload)
          if (result) {
            return
          }
          await updateUser({ userId })
          secureLocalStorage.removeItem(SMASH_SESSION)
          await createUserSession()
        } else {
          secureLocalStorage.removeItem("smashAuthToken")
          secureLocalStorage.removeItem(SMASH_SESSION)
        }
      } else {
        let response = null
        let userId = -1
        const authToken = secureLocalStorage.getItem("smashAuthToken")
        const isSsoRequested = queryParams.get("sso") === "true"

        if (
          isSsoRequested ||
          (!authToken && Number(process.env.REACT_APP_LOGIN_VERSION) === 1)
        ) {
          if (!isInvoicePage) {
            response = await exchangeToken()
          }
        } else if (authToken && authToken !== "") {
          let userData = null
          let decodedPayload = null
          try {
            decodedPayload = jwtDecode(authToken)
            userData = decodedPayload
            userId = decodedPayload?.id || -1
          } catch (error) {
            console.error("Token inválido", error)
          }
          const result = await validateEmbedModeMismatch(decodedPayload)
          if (result) {
            return
          }
          if (userId > 0) {
            response = {
              status: true,
              token: authToken,
              user: {
                ...userData,
                userId: Number.parseInt(userId, 10),
              },
            }
          }
        }
        logger.log("~ exchangeToken:", response)
        logger.log("~ exchangeToken user:", response?.user)

        if (response?.status) {
          const token = response?.token
          const userTypeId = response?.user?.userTypeId || -1
          userId = response?.user?.userId || -1
          const redirectView = getPath(userTypeId)
          if (!redirectView) {
            logger.log("Invalid user type, redirect to login")
            secureLocalStorage.removeItem("smashAuthToken")
            secureLocalStorage.removeItem(SMASH_SESSION)
            window.location.href = "/login?exchange_token=invalid"
            return
          }
          // update user data
          secureLocalStorage.setItem("smashAuthToken", token)
          // check user session
          const session = secureLocalStorage.getItem(SMASH_SESSION)
          if (session) {
            const userSessionData = session
            logger.log("~ userSessionData:", userSessionData)
            if (userSessionData?.userId !== userId) {
              secureLocalStorage.removeItem(SMASH_SESSION)
            }
          }
          if (userId > 0) {
            await updateUser({ userId })
          } else {
            await checkLanguage(2)
          }
          if (queryParams.get("sso")) {
            const url = new URL(window.location.href)
            url.searchParams.delete("sso")
            window.history.replaceState({}, document.title, url.toString())
          }
        } else {
          await checkLanguage(2)
          if (isSsoRequested) {
            logger.log(
              "Invalid exchange token response during SSO, clearing session and redirecting to login"
            )
            secureLocalStorage.removeItem("smashAuthToken")
            secureLocalStorage.removeItem(SMASH_SESSION)
            const path = window.location.pathname.toLowerCase()
            const isPublicRoute =
              path === "/login" || path.startsWith("/login/")
            if (!isPublicRoute) {
              window.location.href = "/login?exchange_token=invalid"
            }
          } else if (
            !isInvoicePage &&
            Number(process.env.REACT_APP_LOGIN_VERSION) === 1
          ) {
            logger.log("Invalid exchange token response, redirect to login")
            secureLocalStorage.removeItem("smashAuthToken")
            secureLocalStorage.removeItem(SMASH_SESSION)
            const path = window.location.pathname.toLowerCase()
            const isPublicRoute =
              path === "/login" || path.startsWith("/login/")
            if (!isPublicRoute) {
              window.location.href = "/login?exchange_token=invalid"
            }
          }
        }
      }
      setIsLoading(false)
    }
    // check if query param session=clear is presen
    const sessionParam = queryParams.get("session")
    if (sessionParam === "clear") {
      clearSession()
    }
    loadLocales()
    getUser().catch((err) => {
      logger.log(err)
      setIsLoading(false)
      if (Number(process.env.REACT_APP_LOGIN_VERSION) === 1) {
        logger.log("unexpected error, redirect to login")
        secureLocalStorage.removeItem("smashAuthToken")
        secureLocalStorage.removeItem(SMASH_SESSION)
        window.location.href = "/login"
      }
    })
  }, [])

  useEffect(() => {
    window.logout = logout
    return () => {
      delete window.logout
    }
  }, [])

  useEffect(() => {
    const check = async () => {
      if (userData?.userId > 0) {
        try {
          const token = secureLocalStorage.getItem("smashAuthToken")
          const decodedPayload = jwtDecode(token)
          const result = await validateEmbedModeMismatch(decodedPayload)
          if (result) {
            return
          }
        } catch (error) {}
        const runningFromIframe = isRunningFromIframe()
        const isCreateAccountPage = window.location.pathname
          .toLowerCase()
          .includes(routes.CREATE_ACCOUNT)
        if (
          Number(process.env.REACT_APP_PROFESSOR_LOGIN_VERSION) === 1 &&
          userData?.userTypeId === USER_TYPES.TEACHER &&
          !runningFromIframe &&
          !isCreateAccountPage &&
          !isSharedRoutePath()
        ) {
          logger.log(
            "Teacher must run in iframe under Login Version 1. Logging out."
          )
          logout()
        }
      }
    }
    check()
  }, [userData?.userId])

  const logout = useCallback(async () => {
    playLogout()
    localStorage.setItem("smash-prefers-dark-mode", "false")
    document.documentElement.classList.remove("dark-mode")
    setMode("light")
    setBgColor("white")
    setFontFamily("Poppins Regular")
    setFontSize("18px")
    const runningFromIframe = isRunningFromIframe()
    if (
      runningFromIframe &&
      typeof window?.parent?.app?.func?.signOut === "function"
    ) {
      window.parent.app.func.signOut()
    }
    secureLocalStorage.removeItem("smashAuthToken")
    secureLocalStorage.removeItem(SMASH_SESSION)
    setUserData({
      userId: -1,
      username: "",
      lastName: "",
      name: "",
      parentCode: "",
      language: 2,
      avatar: "https://login.smasheducation.com/avatars//default.png",
      allowTutorials: true,
    })
    // clear all local storage items with prefix LOCAL_STORAGE_PREFIX
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(LOCAL_STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
    try {
      const keys = await storage.keys()
      const deletePromises = keys
        .filter((key) => {
          const isGlobal =
            key.includes("global_dictionary") ||
            key.includes("i18next_res") ||
            key.includes("global_languages")
          return key.startsWith(LOCAL_STORAGE_PREFIX) && !isGlobal
        })
        .map((key) => storage.removeItem(key))
      await Promise.all(deletePromises)
    } catch (error) {
      console.log("failed to clear storage", error)
    }
    Sentry.setUser(null)
    try {
      await checkLanguage(2)
    } catch (error) {
      console.warn("Failed to reset language to English on logout", error)
    }
    if (Number(process.env.REACT_APP_LOGIN_VERSION) === 2) {
      navigate("/login")
    } else {
      window.location.href = "/login"
    }
  }, [playLogout, checkLanguage, navigate])

  const clearSession = async () => {
    secureLocalStorage.removeItem("smashAuthToken")
    secureLocalStorage.removeItem(SMASH_SESSION)
    // clear all local storage items with prefix LOCAL_STORAGE_PREFIX
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(LOCAL_STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
    try {
      const keys = await storage.keys()
      const deletePromises = keys
        .filter((key) => {
          const isGlobal =
            key.includes("global_dictionary") ||
            key.includes("i18next_res") ||
            key.includes("global_languages")
          return key.startsWith(LOCAL_STORAGE_PREFIX) && !isGlobal
        })
        .map((key) => storage.removeItem(key))
      await Promise.all(deletePromises)
    } catch (error) {
      console.log("failed to clear storage", error)
    }
    // remove from query pram session=clear
    const url = new URL(window.location.href)
    url.searchParams.delete("session")
    window.history.replaceState({}, document.title, url.toString())
  }

  const getPath = (userTypeId) => {
    const runningFromIframe = isRunningFromIframe()
    if (runningFromIframe) return true
    const isInvoicePage = window.location.pathname
      .toLowerCase()
      .includes("/payments/invoice")
    if (isInvoicePage) return true
    if (userTypeId === 6) {
      if (Number(process.env.REACT_APP_LOGIN_VERSION) === 2) {
        // handle by react project
        return true
      } else {
        const currentPage = window.location.pathname
          .split("/")
          .pop()
          .toLowerCase()
        if (currentPage.includes("announcements")) {
          window.location.href =
            "/professor/announcements" + window.location.search
          return false
        } else {
          window.location.href = "/professor/groups"
          return false
        }
      }
    } else if (userTypeId === 10 || userTypeId === 1) {
      if (process.env.NODE_ENV === "development" && userTypeId === 10) {
        return true
      }
      window.location.href = "/professor/admin/index"
      return false
    } else if (userTypeId === 11) {
      window.location.href = "/institute/Usage.aspx"
      return false
    }
    return true
  }

  const deleteStorageKeys = async (userId) => {
    if (!userId) {
      return
    }

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(LOCAL_STORAGE_PREFIX)) {
        const parts = key.split("_")
        const keyUserId = parts[parts.length - 1]
        if (/^-?\d+$/.test(keyUserId) && keyUserId !== String(userId)) {
          localStorage.removeItem(key)
        }
      }
    })

    try {
      const keys = await storage.keys()
      const deletePromises = keys
        .filter((key) => {
          if (key.startsWith(LOCAL_STORAGE_PREFIX)) {
            const parts = key.split("_")
            const keyUserId = parts[parts.length - 1]
            const isMatch =
              /^-?\d+$/.test(keyUserId) && keyUserId !== String(userId)
            return isMatch
          }
          return false
        })
        .map((key) => storage.removeItem(key))
      await Promise.all(deletePromises)
    } catch (error) {
      console.log("failed to clear storage", error)
    }
  }

  const values = useMemo(
    () => ({
      userData,
      isLoading,
      checkLanguage,
      updateUserData,
      logout,
      updateUser,
      deleteStorageKeys,
      cacheRewardsNotifications,
      setCacheRewardsNotifications,
    }),
    [userData, isLoading, cacheRewardsNotifications]
  )

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export default AuthProvider
